import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import authHeader from "../services/auth-header";

const API = process.env.REACT_APP_API_URL;

export default function BoardSuperAdmin() {
  const [admins, setAdmins] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tab, setTab] = useState("admins"); // admins | locations
  const [newAdmin, setNewAdmin] = useState({ username: "", email: "", password: "" });
  const [newLocation, setNewLocation] = useState({ name: "", ipAddress: "" });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [newFunction, setNewFunction] = useState({ name: "", action: "", type: "BUTTON" });
  const [loading, setLoading] = useState(true);

  const loadData = () => {
    return Promise.all([
      axios.get(`${API}/superadmin/admins`, { headers: authHeader() }),
      axios.get(`${API}/superadmin/locations`, { headers: authHeader() }),
    ]).then(([a, l]) => {
      setAdmins(a.data);
      setLocations(l.data);
    }).catch(() => toast.error("Error cargando datos"));
  };

  useEffect(() => {
    loadData().finally(() => setLoading(false));
  }, []);

  const createAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/admins`,
        { ...newAdmin, role: ["ROLE_ADMIN"] },
        { headers: authHeader() }
      );
      toast.success("Admin creado correctamente");
      setNewAdmin({ username: "", email: "", password: "" });
      const res = await axios.get(`${API}/superadmin/admins`, { headers: authHeader() });
      setAdmins(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creando admin");
    }
  };

  const createLocation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/locations`, newLocation, { headers: authHeader() });
      toast.success("Comunidad creada");
      setNewLocation({ name: "", ipAddress: "" });
      const res = await axios.get(`${API}/superadmin/locations`, { headers: authHeader() });
      setLocations(res.data);
      if (selectedLocation) {
        const updated = res.data.find(l => l.id === selectedLocation.id);
        if (updated) setSelectedLocation(updated);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creando comunidad");
    }
  };

  const addFunction = async (e) => {
    e.preventDefault();
    if (!selectedLocation) return;
    try {
      await axios.post(
        `${API}/superadmin/locations/${selectedLocation.id}/functions`,
        newFunction,
        { headers: authHeader() }
      );
      toast.success("Función añadida");
      setNewFunction({ name: "", action: "", type: "BUTTON" });
      const res = await axios.get(`${API}/superadmin/locations`, { headers: authHeader() });
      setLocations(res.data);
      const updated = res.data.find(l => l.id === selectedLocation.id);
      if (updated) setSelectedLocation(updated);
    } catch (err) {
      toast.error("Error añadiendo función");
    }
  };

  const removeFunction = async (locationFunctionId) => {
    if (!selectedLocation) return;
    try {
      await axios.delete(
        `${API}/superadmin/locations/${selectedLocation.id}/functions/${locationFunctionId}`,
        { headers: authHeader() }
      );
      toast.success("Función eliminada");
      const res = await axios.get(`${API}/superadmin/locations`, { headers: authHeader() });
      setLocations(res.data);
      const updated = res.data.find(l => l.id === selectedLocation.id);
      if (updated) setSelectedLocation(updated);
    } catch (err) {
      toast.error("Error eliminando función");
    }
  };

  const assignAdmin = async (locationId, adminId) => {
    if (!adminId) return;
    try {
      await axios.post(`${API}/superadmin/locations/${locationId}/assign/${adminId}`,
        {}, { headers: authHeader() }
      );
      toast.success("Admin asignado");
      const res = await axios.get(`${API}/superadmin/locations`, { headers: authHeader() });
      setLocations(res.data);
    } catch (err) {
      toast.error("Error asignando admin");
    }
  };

  if (loading) return <div className="main-content"><div className="empty-state"><span className="spinner" /></div></div>;

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Panel SuperAdmin</h1>
        <p className="dashboard-subtitle">Gestión global de administradores y comunidades</p>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <button className={`btn-action${tab === "admins" ? "" : " secondary"}`} onClick={() => setTab("admins")}>
          Administradores ({admins.length})
        </button>
        <button className={`btn-action${tab === "locations" ? "" : " secondary"}`} onClick={() => setTab("locations")}>
          Comunidades ({locations.length})
        </button>
      </div>

      {/* ── TAB: ADMINS ── */}
      {tab === "admins" && (
        <div className="admin-layout">
          <div className="admin-sidebar">
            <p className="location-title">Admins actuales</p>
            {admins.length === 0 && <p style={{ color: "var(--muted)", fontSize: 14 }}>Sin admins creados</p>}
            {admins.map(a => (
              <div key={a.id} className="user-item">
                <span className="user-name">{a.username}</span>
                <span className="user-email">{a.email}</span>
              </div>
            ))}
          </div>
          <div className="admin-main">
            <div className="location-card">
              <p className="location-title">Crear nuevo admin</p>
              <form onSubmit={createAdmin}>
                <div className="form-group">
                  <label>Usuario</label>
                  <input type="text" required value={newAdmin.username}
                    onChange={e => setNewAdmin(p => ({ ...p, username: e.target.value }))}
                    placeholder="gestor_fincas1" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" required value={newAdmin.email}
                    onChange={e => setNewAdmin(p => ({ ...p, email: e.target.value }))}
                    placeholder="gestor@fincas.com" />
                </div>
                <div className="form-group">
                  <label>Contraseña temporal</label>
                  <input type="password" required value={newAdmin.password}
                    onChange={e => setNewAdmin(p => ({ ...p, password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres" />
                </div>
                <button type="submit" className="btn-action" style={{ marginTop: 8 }}>
                  Crear admin
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: COMUNIDADES ── */}
      {tab === "locations" && (
        <div className="admin-layout">
          {/* Sidebar: lista de comunidades */}
          <div className="admin-sidebar">
            <p className="location-title">Comunidades</p>
            {locations.length === 0 && <p style={{ color: "var(--muted)", fontSize: 14 }}>Sin comunidades</p>}
            {locations.map(loc => (
              <button
                key={loc.id}
                className={`user-item${selectedLocation?.id === loc.id ? " active" : ""}`}
                onClick={() => setSelectedLocation(loc)}
              >
                <span className="user-name">{loc.name}</span>
                <span className="user-email">{loc.ipAddress || "Sin IP"}</span>
              </button>
            ))}

            {/* Crear comunidad */}
            <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              <p className="location-title" style={{ fontSize: 13 }}>Nueva comunidad</p>
              <form onSubmit={createLocation}>
                <div className="form-group">
                  <input type="text" required value={newLocation.name}
                    onChange={e => setNewLocation(p => ({ ...p, name: e.target.value }))}
                    placeholder="Nombre de la comunidad" />
                </div>
                <div className="form-group">
                  <input type="text" value={newLocation.ipAddress}
                    onChange={e => setNewLocation(p => ({ ...p, ipAddress: e.target.value }))}
                    placeholder="IP del cliente (opcional)" />
                </div>
                <button type="submit" className="btn-action" style={{ width: "100%", fontSize: 13 }}>
                  Crear comunidad
                </button>
              </form>
            </div>
          </div>

          {/* Main: detalle de comunidad seleccionada */}
          <div className="admin-main">
            {!selectedLocation ? (
              <div className="empty-state">
                <div className="empty-state-icon">👈</div>
                <p>Selecciona una comunidad para gestionar sus funciones</p>
              </div>
            ) : (
              <>
                <div className="admin-user-header">
                  <h2 className="admin-user-title">{selectedLocation.name}</h2>
                  <span className="admin-user-email">{selectedLocation.ipAddress || "Sin IP configurada"}</span>
                </div>

                {/* Asignar admin */}
                <div className="location-card" style={{ marginBottom: 16 }}>
                  <p className="location-title" style={{ fontSize: 13 }}>Admin asignado</p>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <select
                      defaultValue=""
                      onChange={e => assignAdmin(selectedLocation.id, e.target.value)}
                      style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", flex: 1 }}
                    >
                      <option value="" disabled>
                        {selectedLocation.adminUsername ? `Admin actual: ${selectedLocation.adminUsername}` : "Asignar admin..."}
                      </option>
                      {admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                    </select>
                  </div>
                </div>

                {/* Funciones actuales */}
                <div className="location-card">
                  <p className="location-title" style={{ fontSize: 13 }}>
                    Funciones ({selectedLocation.functions?.length || 0})
                  </p>
                  {(!selectedLocation.functions || selectedLocation.functions.length === 0) ? (
                    <p style={{ color: "var(--muted)", fontSize: 13 }}>Sin funciones. Añade una abajo.</p>
                  ) : (
                    <div className="action-grid">
                      {selectedLocation.functions.map(f => (
                        <div key={f.locationFunctionId} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="btn-action secondary" style={{ flex: 1, textAlign: "left", cursor: "default" }}>
                            {f.action || f.name}
                          </span>
                          <button
                            className="btn-action"
                            style={{ background: "var(--error-bg, #3a1a1a)", color: "var(--error, #f87171)", padding: "6px 12px", fontSize: 12 }}
                            onClick={() => removeFunction(f.locationFunctionId)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Añadir función */}
                  <form onSubmit={addFunction} style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                    <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>Añadir función</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <input type="text" required value={newFunction.name}
                        onChange={e => setNewFunction(p => ({ ...p, name: e.target.value }))}
                        placeholder="Nombre interno (ej: abrir_puerta)"
                        style={{ flex: 1, minWidth: 150 }} />
                      <input type="text" required value={newFunction.action}
                        onChange={e => setNewFunction(p => ({ ...p, action: e.target.value }))}
                        placeholder="Texto del botón (ej: Abrir puerta)"
                        style={{ flex: 1, minWidth: 150 }} />
                      <button type="submit" className="btn-action" style={{ whiteSpace: "nowrap" }}>
                        Añadir
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
